import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTopBar } from '@/contexts/TopBarContext'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { adminApi } from '@/api/admin'
import { DateFilter, getRange, type Preset } from './DateFilter'
import { OverviewTab } from './OverviewTab'
import { CoursesTab } from './CoursesTab'
import { TrainersTab } from './TrainersTab'
import { ClientsTab } from './ClientsTab'
import { FinanceTab } from './FinanceTab'

type Tab = 'overview' | 'courses' | 'trainers' | 'clients' | 'finance'

const TABS: { value: Tab; label: string }[] = [
  { value: 'overview',  label: 'Overview' },
  { value: 'courses',   label: 'Courses' },
  { value: 'trainers',  label: 'Trainers' },
  { value: 'clients',   label: 'Clients' },
  { value: 'finance',   label: 'Finance' },
]

export default function AnalyticsPage() {
  useTopBar(<span className="text-sm font-medium text-gray-900">Analytics</span>)
  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const [tab,        setTab]        = useState<Tab>('overview')
  const [preset,     setPreset]     = useState<Preset>('12m')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo,   setCustomTo]   = useState('')

  const navigate = useNavigate()
  const { from, to } = getRange(preset, customFrom, customTo)
  const isPro = profile?.planTier === 'Pro'

  return (
    <div className="space-y-6">
      {!isPro ? (
        <div className="bg-white border border-amber-200 rounded-2xl p-12 text-center shadow-sm">
          <Lock size={32} className="mx-auto text-amber-400 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Analytics is a Pro feature</h2>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
            Upgrade to Pro to unlock detailed performance analytics across courses, trainers, clients, and finance.
          </p>
          <Button onClick={() => navigate('/admin/account?tab=billing')}>
            Upgrade to Pro
          </Button>
        </div>
      ) : (
        <>
          <DateFilter
            preset={preset} setPreset={setPreset}
            customFrom={customFrom} setCustomFrom={setCustomFrom}
            customTo={customTo} setCustomTo={setCustomTo}
          />

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit overflow-x-auto">
            {TABS.map(t => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  tab === t.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview'  && <OverviewTab  from={from} to={to} />}
          {tab === 'courses'   && <CoursesTab   from={from} to={to} />}
          {tab === 'trainers'  && <TrainersTab  from={from} to={to} />}
          {tab === 'clients'   && <ClientsTab   from={from} to={to} />}
          {tab === 'finance'   && <FinanceTab   from={from} to={to} />}
        </>
      )}
    </div>
  )
}
